import React from 'react';
import { X, Check, Package, Phone, Users, Settings, Award, Headphones, HeartHandshake, Wrench, Shield, Clock } from 'lucide-react';

const ComparisonSection = () => {
  const standardFeatures = [
    { icon: Package, text: 'Standardpakete von der Stange', negative: true },
    { icon: Headphones, text: 'Callcenter-Betreuung', negative: true },
    { icon: Users, text: 'Wechselnde Subunternehmer', negative: true },
    { icon: Settings, text: 'Geringe Systemintegration', negative: true },
    { icon: Shield, text: 'Wenig Qualitätsmanagement', negative: true },
    { icon: Clock, text: 'Projekt abgeschlossen = Kontakt beendet', negative: true }
  ];

  const sunprojectsFeatures = [
    { icon: Award, text: 'Expertengeführte, individuelle Planung', negative: false },
    { icon: Phone, text: 'Direkte Ansprechpartner', negative: false },
    { icon: HeartHandshake, text: 'Persönliche Projektverantwortung', negative: false },
    { icon: Settings, text: 'Komplette Systemintegration (PV + Speicher + Wallbox + WP)', negative: false },
    { icon: Shield, text: 'Qualitäts-, Reklamations- & Produktmanagement-Erfahrung', negative: false },
    { icon: Wrench, text: 'Langfristige Betreuung & Service', negative: false }
  ];

  return (
    <section data-testid="comparison-section" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-sun-orange/10 text-sun-orange rounded-full text-sm font-semibold mb-4">
            Der Unterschied
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Warum Sunprojects –
            <span className="text-sun-orange"> im Vergleich</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Nicht jeder Solaranbieter arbeitet gleich. Erfahren Sie, was uns von 
            Standardanbietern unterscheidet – und warum das für Ihr Projekt entscheidend ist.
          </p>
        </div>

        {/* Comparison Cards */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Standard Provider Card */}
          <div className="relative bg-white rounded-3xl p-8 lg:p-10 border-2 border-slate-200 shadow-lg">
            <div className="absolute -top-4 left-8">
              <span className="bg-slate-200 text-slate-600 px-4 py-2 rounded-full text-sm font-semibold">
                Standardanbieter
              </span>
            </div>
            
            <div className="mt-4 space-y-5">
              {standardFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div 
                    key={index} 
                    data-testid={`standard-feature-${index}`}
                    className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl"
                  >
                    <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <X className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <span className="text-slate-700 font-medium">{feature.text}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sunprojects Card */}
          <div className="relative z-20 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-3xl p-8 lg:p-10 border-2 border-sun-orange/30 shadow-xl">
            <div className="absolute -top-4 left-8 z-30">
              <span className="bg-sun-orange text-white px-4 py-2 rounded-full text-sm font-bold">
                Sunprojects
              </span>
            </div>
            
            {/* Highlight Border Effect */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-sun-orange/10 via-transparent to-transparent pointer-events-none" />
            
            <div className="mt-4 space-y-5 relative z-10">
              {sunprojectsFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div 
                    key={index} 
                    data-testid={`sunprojects-feature-${index}`}
                    className="flex items-start gap-4 p-4 bg-white/10 rounded-xl backdrop-blur-sm"
                  >
                    <div className="w-10 h-10 bg-sun-orange rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <span className="text-white font-medium">{feature.text}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-4 bg-sun-orange/10 border border-sun-orange/20 rounded-2xl">
            <div className="w-12 h-12 bg-sun-orange rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <p className="font-bold text-slate-900">Die Frage „Warum nicht XY?"</p>
              <p className="text-slate-600 text-sm">beantwortet sich mit einem Blick auf unsere Arbeitsweise.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
